import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Extend Window interface for fbq
declare global {
  interface Window {
    fbq?: (...args: any[]) => void;
  }
}

// ----------------------------------------------------------------------

/**
 * Hook to track page views with Meta Pixel on route changes in React SPA
 * This is necessary because React SPAs don't trigger full page reloads
 */
export function useMetaPageView() {
  const location = useLocation();

  useEffect(() => {
    if (window.fbq) {
      window.fbq('track', 'PageView');
    }
  }, [location.pathname]);
}

// ----------------------------------------------------------------------

/**
 * Meta Pixel event tracking utilities
 * Use these functions to track custom events
 */
export const metaPixel = {
  /**
   * Track when a user views a product/content
   */
  trackViewContent: (params: {
    content_ids: string[];
    content_type?: string;
    value?: number;
    currency?: string;
  }) => {
    window.fbq?.('track', 'ViewContent', {
      content_type: 'product',
      currency: 'QAR',
      ...params,
    });
  },

  /**
   * Track when a user adds an item to cart
   */
  trackAddToCart: (params: {
    content_ids: string[];
    content_type?: string;
    value?: number;
    currency?: string;
  }) => {
    window.fbq?.('track', 'AddToCart', {
      content_type: 'product',
      currency: 'QAR',
      ...params,
    });
  },

  /**
   * Track purchase - ONLY call after payment is confirmed!
   */
  trackPurchase: (params: { value: number; currency?: string }) => {
    window.fbq?.('track', 'Purchase', {
      currency: 'QAR',
      ...params,
    });
  },

  /**
   * Track when user initiates checkout
   */
  trackInitiateCheckout: (params?: {
    content_ids?: string[];
    value?: number;
    currency?: string;
    num_items?: number;
  }) => {
    window.fbq?.('track', 'InitiateCheckout', {
      currency: 'QAR',
      ...params,
    });
  },

  /**
   * Track when user adds payment info
   */
  trackAddPaymentInfo: () => {
    window.fbq?.('track', 'AddPaymentInfo');
  },

  /**
   * Track when user completes registration
   */
  trackCompleteRegistration: () => {
    window.fbq?.('track', 'CompleteRegistration');
  },

  /**
   * Track contact/lead form submissions
   */
  trackLead: () => {
    window.fbq?.('track', 'Lead');
  },

  /**
   * Track custom event
   */
  trackCustom: (eventName: string, params?: Record<string, any>) => {
    window.fbq?.('trackCustom', eventName, params);
  },
};
